"use client";

import * as React from "react";

import { parseHslToHex } from "@/lib/color.utils";
import { ToastProvider } from "@heroui/react";
import { HeroUIProvider } from "@heroui/system";

type HeroProviderProps = {
  children: React.ReactNode;
  navigate?: (path: string) => void;
  useHref?: (path: string) => string;
}

function HeroProvider({ children, navigate, useHref }: HeroProviderProps) {
  const [theme, setTheme] = React.useState<"light" | "dark">("light");

  const setHtmlElementTheme = (theme: "light" | "dark") => {
    if (theme === "dark") {
      document.documentElement.classList.remove("light");
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
    }
  };

  const updateMetaTags = () => {
    const hslColor = getComputedStyle(document.documentElement).getPropertyValue('--heroui-background').trim();
    const themeColor = parseHslToHex(hslColor);
    const statusBarStyle = themeColor;

    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement("meta");
      themeColorMeta.setAttribute("name", "theme-color");
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute("content", themeColor);

    let statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!statusBarMeta) {
      statusBarMeta = document.createElement("meta");
      statusBarMeta.setAttribute("name", "apple-mobile-web-app-status-bar-style");
      document.head.appendChild(statusBarMeta);
    }
    statusBarMeta.setAttribute("content", statusBarStyle);

    let capableMeta = document.querySelector('meta[name="apple-mobile-web-app-capable"]');
    if (!capableMeta) {
      capableMeta = document.createElement("meta");
      capableMeta.setAttribute("name", "apple-mobile-web-app-capable");
      document.head.appendChild(capableMeta);
    }
    capableMeta.setAttribute("content", "yes");
  };

  React.useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const newTheme = mediaQuery.matches ? "dark" : "light";

    setTheme(newTheme);
    setHtmlElementTheme(newTheme);
    updateMetaTags();

    const handler = (e: MediaQueryListEvent) => {
      const newTheme = e.matches ? "dark" : "light";
      setTheme(newTheme);
      setHtmlElementTheme(newTheme);
      updateMetaTags();
    };

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, []);

  return (
    <HeroUIProvider className={`${theme} text-foreground bg-background`} navigate={navigate} useHref={useHref} locale="it-IT">
      <ToastProvider />
      {children}
    </HeroUIProvider>
  );
}

export default HeroProvider;
